import { ACTUAL_HOLDINGS_SOURCE, constantSourceRequest, holdingsSourceKey } from '../historicalPerformanceBasis';
import { isIsoDate } from './exploreFormat';

export const EXPLORE_CONTRACT = 'performance-explore-factor-replay.v1';
export const MAX_PORTFOLIOS = 6;
// Actual market rows shown beside the portfolios; they never count as one.
export const MAX_MARKETS = 3;
// The table holds one grid request: 40 columns, the found periods included.
export const MAX_TABLE_COLUMNS = 40;
// Your events, as services/explore_event_service.py stores them. Notes hold
// a standard event's longest description, so a copy keeps it whole.
export const EVENT_TITLE_MAX = 120;
export const EVENT_NOTES_MAX = 4000;
// How many of the baseline's worst, and best, non-overlapping week, month and
// quarter windows the scan finds for each.
export const FOUND_COUNT = 3;
// The found periods' sides, as the grid's column kinds, in the order shown.
const FOUND_SIDES = Object.freeze({ worst: 'Worst', best: 'Best' });

// The saved arrangement (`appearance_preferences.compareExploreColumns`): the
// events in the table, by id, and how they sort. Mirrors
// `_validate_compare_explore_columns` in
// functions/services/appearance_preferences_service.py: preferences save in
// one request, so a shape this produces but the backend rejects would make
// every save fail.
export const SAVED_COLUMNS_VERSION = 2;
const MAX_COLUMN_ID_LENGTH = 100;
const COLUMN_ID = /^(?:library:[a-z0-9]+(?:-[a-z0-9]+)*|event:[1-9]\d{0,18}|(?:worst|best):(?:5|21|63):(?:[1-9]|10))$/;
const SLUG = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;
const MAX_SLUG_LENGTH = 80;

const libraryColumnId = (slug) => `library:${slug}`;
export const eventColumnId = (eventId) => `event:${eventId}`;
// A found period's id names its side, horizon and rank ("best:21:1").
export const isFoundColumnId = (id) => Object.keys(FOUND_SIDES).some((side) => id.startsWith(`${side}:`));
// The grid keys every requested period by its dates, so a standard event and
// one of yours with the same window share their cells.
const periodKey = (start, end) => `custom:${start}:${end}`;

const isPlainObject = (value) => value != null && typeof value === 'object' && !Array.isArray(value);
const byWindow = (left, right) => left.start_date.localeCompare(right.start_date) || left.end_date.localeCompare(right.end_date);

/**
 * The published stress library from `GET /api/stress-library`, or null when
 * the response is malformed. Episodes without a usable slug, name or window
 * are dropped, and so is a second episode with the same slug or window.
 */
export function normalizeStressLibrary(response) {
    if (!isPlainObject(response) || !Array.isArray(response.episodes)
        || !(response.library_version === null || Number.isInteger(response.library_version))) return null;
    const slugs = new Set();
    const windows = new Set();
    const episodes = [];
    for (const raw of response.episodes) {
        if (!isPlainObject(raw) || typeof raw.slug !== 'string' || !SLUG.test(raw.slug) || raw.slug.length > MAX_SLUG_LENGTH
            || typeof raw.name !== 'string' || !raw.name.trim()
            || !isIsoDate(raw.window_start) || !isIsoDate(raw.window_end) || raw.window_start > raw.window_end) continue;
        const window = periodKey(raw.window_start, raw.window_end);
        if (slugs.has(raw.slug) || windows.has(window)) continue;
        slugs.add(raw.slug);
        windows.add(window);
        episodes.push({
            slug: raw.slug, name: raw.name.trim(), category: typeof raw.category === 'string' ? raw.category : '',
            window_start: raw.window_start, window_end: raw.window_end,
            description: typeof raw.description === 'string' ? raw.description : '',
        });
    }
    return { version: response.library_version, episodes };
}

// One of your events as the events routes return it, or null when malformed.
export function normalizeExploreEvent(raw) {
    if (!isPlainObject(raw) || !Number.isSafeInteger(raw.event_id) || raw.event_id < 1
        || typeof raw.title !== 'string' || !raw.title.trim()
        || !isIsoDate(raw.start_date) || !isIsoDate(raw.end_date) || raw.start_date > raw.end_date) return null;
    return {
        id: raw.event_id, title: raw.title.trim(), start_date: raw.start_date, end_date: raw.end_date,
        notes: typeof raw.notes === 'string' ? raw.notes : '',
    };
}

// Your events from `GET /api/performance/explore/events` and how many you
// can keep, or null when the response is malformed.
export function normalizeExploreEvents(response) {
    if (!isPlainObject(response) || !Array.isArray(response.events) || !Number.isInteger(response.max_events)) return null;
    return { events: response.events.map(normalizeExploreEvent).filter(Boolean), maxEvents: response.max_events };
}

// Before any arrangement is saved: the standard library by date, as much of
// it as the table holds.
export function defaultColumnState(library) {
    return {
        version: SAVED_COLUMNS_VERSION,
        columns: library.slice(0, MAX_TABLE_COLUMNS).map((episode) => libraryColumnId(episode.slug)),
        sort: 'date',
    };
}

// A valid saved arrangement in canonical form, or null (nothing remembered).
export function normalizeCompareExploreColumns(value) {
    if (!isPlainObject(value) || value.version !== SAVED_COLUMNS_VERSION || !Array.isArray(value.columns)) return null;
    const columns = [...new Set(value.columns.filter(
        (id) => typeof id === 'string' && id.length <= MAX_COLUMN_ID_LENGTH && COLUMN_ID.test(id),
    ))].slice(0, MAX_TABLE_COLUMNS);
    return { version: SAVED_COLUMNS_VERSION, columns, sort: value.sort === 'baseline' ? 'baseline' : 'date' };
}

/**
 * Every event that can be a column, by box: the standard library, your
 * events and, once a grid has loaded, the baseline's worst and best periods
 * found by its scans. Found periods come in the order Add all puts them in
 * the table: by rank, each rank week to quarter with the worst before the
 * best, so a nearly full table still gets both sides. `responseId` keys
 * the column's cells in a grid row. A found period is named for `foundFor`,
 * the scanned portfolio ("Worst month #1 for Actual"), since its dates
 * change with the baseline; its chip, in the box already titled for that
 * portfolio, keeps the short `chipName`.
 */
export function columnCatalogue(library, events, grid = null, foundFor = '') {
    const standard = library.map((episode) => ({
        id: libraryColumnId(episode.slug), tag: 'standard', name: episode.name, description: episode.description,
        start_date: episode.window_start, end_date: episode.window_end, responseId: periodKey(episode.window_start, episode.window_end),
    }));
    const yours = events.slice().sort((left, right) => byWindow(left, right) || left.id - right.id).map((event) => ({
        id: eventColumnId(event.id), tag: 'yours', eventId: event.id, name: event.title, notes: event.notes,
        start_date: event.start_date, end_date: event.end_date, responseId: periodKey(event.start_date, event.end_date),
    }));
    const sides = Object.keys(FOUND_SIDES);
    const found = (grid?.columns || []).filter((column) => sides.includes(column.kind)).map((column) => ({
        id: column.column_id, tag: 'found', side: column.kind, name: `${column.label} for ${foundFor}`, chipName: column.label,
        start_date: column.start_date, end_date: column.end_date,
        horizonDays: column.horizon_days, rank: column.rank, responseId: column.column_id,
    })).sort((left, right) => left.rank - right.rank || left.horizonDays - right.horizonDays
        || sides.indexOf(left.side) - sides.indexOf(right.side));
    return { standard, yours, found, byId: new Map([...standard, ...yours, ...found].map((column) => [column.id, column])) };
}

// The table's columns without those known to be gone: an episode the
// published library no longer holds, or an event deleted since. Until a read
// has loaded, nothing of its kind is dropped.
export function liveColumnIds(columnIds, catalogue, { libraryLoaded = false, eventsLoaded = false } = {}) {
    return columnIds.filter((id) => {
        if (catalogue.byId.has(id)) return true;
        if (id.startsWith('library:')) return !libraryLoaded;
        if (id.startsWith('event:')) return !eventsLoaded;
        return true;
    });
}

/**
 * Puts events in the table in the order given, while it has room. The ones
 * that did not fit come back as `skipped`, so the caller can say so.
 */
export function addColumns(state, ids) {
    const columns = [...state.columns];
    const skipped = [];
    ids.forEach((id) => {
        if (columns.includes(id)) return;
        if (columns.length < MAX_TABLE_COLUMNS) columns.push(id);
        else skipped.push(id);
    });
    return { state: { ...state, columns }, skipped };
}

export function removeColumns(state, ids) {
    const removed = new Set(ids);
    return { ...state, columns: state.columns.filter((id) => !removed.has(id)) };
}

/**
 * The periods one grid request carries: the window of each standard event
 * and each of yours in the table, once, by date. The worst and best periods
 * come from the same request's scan.
 */
export function requestPeriods(catalogue, columnIds) {
    const windows = new Map();
    columnIds.forEach((id) => {
        const column = catalogue.byId.get(id);
        if (column && column.tag !== 'found') windows.set(column.responseId, { start_date: column.start_date, end_date: column.end_date });
    });
    return [...windows.values()].sort(byWindow);
}

// The grid request: each portfolio once (Actual without a source), the
// periods, the baseline's worst and best periods from the same scan, and any
// markets.
export function buildGridRequest(sources, periods, markets = []) {
    return {
        portfolios: sources.map((source) => {
            const request = constantSourceRequest(source);
            return request ? { constant_portfolio_source: request } : {};
        }),
        periods,
        worst_periods: { portfolio_index: 0, count: FOUND_COUNT },
        best_periods: { portfolio_index: 0, count: FOUND_COUNT },
        ...(markets.length ? { markets } : {}),
    };
}

/**
 * The markets a comparison can show, from `GET /api/performance/explore/markets`,
 * or null when the response is malformed: each ETF's key (its ticker), its
 * label ("S&P 500 (SPY)") and whether its history can be read.
 */
export function normalizeExploreMarkets(response) {
    if (!isPlainObject(response) || response.success !== true || !Array.isArray(response.markets)) return null;
    const keys = new Set();
    const markets = [];
    for (const raw of response.markets) {
        if (!isPlainObject(raw) || typeof raw.market !== 'string' || !/^[A-Z]{1,8}$/.test(raw.market) || keys.has(raw.market)
            || typeof raw.label !== 'string' || !raw.label.trim()) continue;
        keys.add(raw.market);
        markets.push({ key: raw.market, label: `${raw.label.trim()} (${raw.market})`, available: raw.available === true });
    }
    return { markets };
}

// Columns by the decade they start in, each group in date order.
export function decadeGroups(columns) {
    const groups = new Map();
    columns.slice().sort(byWindow).forEach((column) => {
        const label = `${column.start_date.slice(0, 3)}0s`;
        if (!groups.has(label)) groups.set(label, []);
        groups.get(label).push(column);
    });
    return [...groups].map(([label, items]) => ({ label, columns: items }));
}

// Found chips in rows by side, worst first, each row in date order (the
// catalogue keeps them in the order Add all uses).
export function foundGroups(columns) {
    return Object.entries(FOUND_SIDES)
        .map(([side, label]) => ({ label, columns: columns.filter((column) => column.side === side).sort(byWindow) }))
        .filter((group) => group.columns.length);
}

export function cellOf(row, column) {
    return row?.available ? row.cells?.[column.responseId] || null : null;
}

export function cellTotal(row, column) {
    const cell = cellOf(row, column);
    return cell?.available && Number.isFinite(cell.total) ? cell.total : null;
}

// A row's number in the chosen basis: its own result, or its gap to the
// baseline (row 0) in the same period.
export function cellValue(row, column, basis, baselineRow) {
    const total = cellTotal(row, column);
    if (basis !== 'gap' || total === null) return total;
    const base = cellTotal(baselineRow, column);
    return base === null ? null : total - base;
}

const TAG_ORDER = Object.freeze({ standard: 0, found: 1, yours: 2 });
const byDate = (left, right) => byWindow(left, right) || TAG_ORDER[left.tag] - TAG_ORDER[right.tag];

// The shown columns, by start date or by the baseline's result (worst
// first; periods without a result last).
export function displayColumns(catalogue, columnIds, sort, baselineRow) {
    const shown = columnIds.map((id) => catalogue.byId.get(id)).filter(Boolean);
    if (sort !== 'baseline' || !baselineRow?.available) return shown.sort(byDate);
    return shown.sort((left, right) => {
        const a = cellTotal(baselineRow, left);
        const b = cellTotal(baselineRow, right);
        if (a === null || b === null) return (a === null) - (b === null) || byDate(left, right);
        return a - b || byDate(left, right);
    });
}

// The portfolios a comparison can hold: Actual, Position Sizing and the saved
// optimized portfolios, the constant sources Performance offers. Labels name
// options in the add field, so they are kept distinct.
export function sourceOptions(savedPortfolios = []) {
    const options = [{ key: holdingsSourceKey(ACTUAL_HOLDINGS_SOURCE), source: ACTUAL_HOLDINGS_SOURCE, label: 'Actual', revision: null }];
    const sizing = savedPortfolios.find((item) => item?.type === 'wi');
    if (sizing) {
        options.push({ key: 'position_sizing', source: { kind: 'position_sizing' }, label: 'Position Sizing', revision: sizing.currentPosSizingStateHash ?? null });
    }
    savedPortfolios
        .filter((item) => item?.type === 'opt' && item.id != null && item.id !== '' && Number.isInteger(Number(item.id)))
        .slice()
        .sort((left, right) => String(left.value || '').localeCompare(String(right.value || ''), undefined, { sensitivity: 'base' }))
        .forEach((item) => {
            const source = { kind: 'optimized', portfolioId: Number(item.id) };
            options.push({ key: holdingsSourceKey(source), source, label: `Opt · ${item.value || `saved portfolio ${item.id}`}`, revision: item.portfolioVersion ?? null });
        });
    const used = new Map();
    return options.map((option) => {
        const count = (used.get(option.label) || 0) + 1;
        used.set(option.label, count);
        return count === 1 ? option : { ...option, label: `${option.label} (${count})` };
    });
}

export function sourceFromKey(key) {
    if (key === 'position_sizing') return { kind: 'position_sizing' };
    const match = /^optimized:(\d+)$/.exec(key || '');
    return match ? { kind: 'optimized', portfolioId: Number(match[1]) } : ACTUAL_HOLDINGS_SOURCE;
}

export function defaultSourceKeys(options) {
    return options.filter((option) => option.key === 'actual' || option.key === 'position_sizing').map((option) => option.key);
}

/**
 * The factor waterfall's steps: the largest contributions (the route sorts
 * them by size), the rest summed into one row so the bars still add up, and
 * the total. Each step is a stacked pair: a transparent `base` (the running
 * total before it) and the signed `size` that moves it to `end`.
 */
export const WATERFALL_FACTOR_ROWS = 7;
export function waterfallSteps(byFactor, result, limit = WATERFALL_FACTOR_ROWS) {
    const factors = (byFactor || [])
        .filter((factor) => Number.isFinite(factor?.contribution))
        .slice()
        .sort((left, right) => Math.abs(right.contribution) - Math.abs(left.contribution));
    const rows = factors.slice(0, limit).map((factor) => ({
        key: `factor:${factor.factor}`, label: String(factor.factor), family: factor.family,
        contribution: factor.contribution, exposure: factor.exposure, factorReturn: factor.factor_return,
    }));
    const rest = factors.slice(limit);
    if (rest.length) {
        rows.push({
            key: 'other', label: `${rest.length} other factor${rest.length === 1 ? '' : 's'}`, other: true,
            contribution: rest.reduce((sum, factor) => sum + factor.contribution, 0),
        });
    }
    let running = 0;
    const steps = rows.map((row) => {
        const base = running;
        running += row.contribution;
        return { ...row, base, size: row.contribution, end: running };
    });
    const total = Number.isFinite(result?.total) ? result.total : running;
    const range = Number.isFinite(result?.specific_range) ? result.specific_range : null;
    steps.push({ key: 'total', label: 'Total', total: true, base: 0, size: total, end: total, contribution: total, range });
    return steps;
}

// Each step's horizontal extent, including the total's range whisker.
export function stepExtent(step) {
    const ends = [step.base, step.end];
    if (step.total && Number.isFinite(step.range)) ends.push(step.end - step.range, step.end + step.range);
    return [Math.min(...ends), Math.max(...ends)];
}

export function waterfallDomain(steps) {
    let lo = 0;
    let hi = 0;
    steps.forEach((step) => {
        const [a, b] = stepExtent(step);
        lo = Math.min(lo, a);
        hi = Math.max(hi, b);
    });
    return lo === hi ? [lo - 0.001, hi + 0.001] : [lo, hi];
}

// The daily path with its stock-specific band (cumulative ± range).
export function pathPoints(dailyPath) {
    return (dailyPath || [])
        .filter((point) => isIsoDate(point?.date) && Number.isFinite(point.cumulative))
        .map((point) => ({
            date: point.date,
            cumulative: point.cumulative,
            daily: Number.isFinite(point.daily) ? point.daily : null,
            range: Number.isFinite(point.specific_range) ? point.specific_range : null,
            band: Number.isFinite(point.specific_range)
                ? [point.cumulative - point.specific_range, point.cumulative + point.specific_range] : null,
        }));
}

export function pathSummary(points) {
    if (!points.length) return null;
    const worst = points.reduce((low, point) => (point.cumulative < low.cumulative ? point : low), points[0]);
    const last = points[points.length - 1];
    return { worst, end: last.cumulative, rangeAtEnd: last.range };
}
